// Trust Relationship Full Detail Component
// Displays complete details for a single trust relationship with paginated instance loading

import { ApckTrustLevelBadge, ApckCountryInfo, ApckThirdpartyFavicon } from './trust_thirdparty_details.js';
import { ApckTrustInstanceDetails } from './trust_thirdparty_details.js';

/**
 * Main Trust Relationship Detail Component
 * Displays full relationship information with paginated trust instances
 */
export const ApckTrustRelationshipDetail = {
    name: 'ApckTrustRelationshipDetail',
    components: {
        'apck-trust-level-badge': ApckTrustLevelBadge,
        'apck-country-info': ApckCountryInfo,
        'apck-thirdparty-favicon': ApckThirdpartyFavicon,
        'apck-trust-instance-details': ApckTrustInstanceDetails
    },
    props: {
        relationshipId: {
            type: String,
            required: true
        }
    },
    data: function() {
        return {
            relationship: null,
            trustInstances: [],
            loading: true,
            loadingMore: false,
            error: null,
            pagination: {
                page: 1,
                limit: 50,
                totalCount: 0,
                hasMore: false
            }
        };
    },
    computed: {
        hasInstances: function() {
            return this.trustInstances.length > 0;
        },
        showLoadMore: function() {
            return this.pagination.hasMore && !this.loadingMore;
        },
        trustDomain: function() {
            return this.relationship?.trust_domain || {};
        },
        vulnerabilities: function() {
            return this.relationship?.vulnerabilities || [];
        },
        highestVulnerabilityImpact: function() {
            return this.relationship?.highest_vulnerability_impact || 'None';
        },
        highestTrustLevel: function() {
            return this.relationship?.highest_trust_level || {};
        }
    },
    mounted: function() {
        this.loadRelationship();
        this.loadTrustInstances();
    },
    methods: {
        loadRelationship: async function() {
            try {
                const response = await fetch(`/trust/relationship/${this.relationshipId}/details`, {
                    headers: {
                        'Accept': 'application/json'
                    }
                });
                
                if (!response.ok) {
                    throw new Error(`Failed to load relationship: ${response.statusText}`);
                }
                
                const data = await response.json();
                this.relationship = data;
                
            } catch (err) {
                console.error('Error loading relationship:', err);
                this.error = err.message;
            }
        },
        
        loadTrustInstances: async function(append = false) {
            try {
                if (append) {
                    this.loadingMore = true;
                } else {
                    this.loading = true;
                }
                
                const page = append ? this.pagination.page + 1 : 1;
                const response = await fetch(
                    `/trust/relationship/${this.relationshipId}/instances?page=${page}&limit=${this.pagination.limit}`,
                    {
                        headers: {
                            'Accept': 'application/json'
                        }
                    }
                );
                
                if (!response.ok) {
                    throw new Error(`Failed to load trust instances: ${response.statusText}`);
                }
                
                const data = await response.json();
                
                if (append) {
                    this.trustInstances = this.trustInstances.concat(data.trust_instances || []);
                } else {
                    this.trustInstances = data.trust_instances || [];
                }
                
                this.pagination = {
                    page: data.page || 1,
                    limit: data.limit || 50,
                    totalCount: data.total_count || 0,
                    hasMore: data.has_more || false
                };
                
            } catch (err) {
                console.error('Error loading trust instances:', err);
                this.error = err.message;
            } finally {
                this.loading = false;
                this.loadingMore = false;
            }
        },
        
        loadMore: function() {
            this.loadTrustInstances(true);
        },
        
        formatDate: function(dateStr) {
            if (!dateStr) return 'N/A';
            const date = new Date(dateStr);
            return date.toLocaleString();
        },
        
        getVulnerabilityClass: function(impact) {
            const impactClasses = {
                'CRITICAL': 'vulnerability-critical',
                'HIGH': 'vulnerability-high',
                'MEDIUM': 'vulnerability-medium',
                'LOW': 'vulnerability-low',
                'INFORMATIONAL': 'vulnerability-info'
            };
            return impactClasses[impact?.toUpperCase()] || 'vulnerability-unknown';
        }
    },
    template: `
        <div class="trust-relationship-detail">
            <ui-card v-if="error" class="error-card">
                <ui-alert type="error">
                    <strong>Error:</strong> {{ error }}
                </ui-alert>
            </ui-card>
            
            <div v-if="loading && !relationship" class="loading-state">
                <ui-spinner size="large"></ui-spinner>
                <ui-text>Loading relationship details...</ui-text>
            </div>
            
            <div v-else-if="relationship" class="relationship-content">
                <!-- Relationship Header -->
                <ui-card class="relationship-header">
                    <ui-card-content>
                        <ui-grid cols="1" gap="large">
                            <div class="header-section">
                                <ui-heading level="2">Trust Relationship Details</ui-heading>
                                <ui-text type="secondary" size="small">
                                    Relationship ID: {{ relationshipId }}
                                </ui-text>
                            </div>
                            
                            <!-- Trust Domain Information -->
                            <div class="trust-domain-info">
                                <ui-heading level="3">Trust Domain</ui-heading>
                                <ui-grid cols="2" gap="medium">
                                    <div>
                                        <ui-label>Hostname:</ui-label>
                                        <ui-text>
                                            <apck-thirdparty-favicon 
                                                :favicon="trustDomain.favicon"
                                                :origin-url="trustDomain.hostname">
                                            </apck-thirdparty-favicon>
                                            <a :href="'https://' + trustDomain.hostname" target="_blank">
                                                {{ trustDomain.hostname }}
                                            </a>
                                        </ui-text>
                                    </div>
                                    <div v-if="trustDomain.country_info">
                                        <ui-label>Location:</ui-label>
                                        <apck-country-info :country-info="trustDomain.country_info"></apck-country-info>
                                    </div>
                                    <div>
                                        <ui-label>First Scanned:</ui-label>
                                        <ui-text>{{ formatDate(trustDomain.first_scanned_at) }}</ui-text>
                                    </div>
                                    <div>
                                        <ui-label>Last Scanned:</ui-label>
                                        <ui-text>{{ formatDate(trustDomain.last_scanned_at) }}</ui-text>
                                    </div>
                                </ui-grid>
                            </div>
                            
                            <!-- Trust Level Information -->
                            <div class="trust-level-section" v-if="highestTrustLevel.level !== undefined">
                                <ui-heading level="3">Highest Trust Level</ui-heading>
                                <apck-trust-level-badge
                                    :level="highestTrustLevel.level"
                                    :description="highestTrustLevel.description"
                                    :show-description="true">
                                </apck-trust-level-badge>
                            </div>
                            
                            <!-- Vulnerabilities Section -->
                            <div class="vulnerabilities-section" v-if="vulnerabilities.length > 0">
                                <ui-heading level="3">
                                    Vulnerabilities
                                    <ui-badge :class="getVulnerabilityClass(highestVulnerabilityImpact)">
                                        Highest Impact: {{ highestVulnerabilityImpact }}
                                    </ui-badge>
                                </ui-heading>
                                <ui-list>
                                    <ui-list-item v-for="(vuln, index) in vulnerabilities" :key="index">
                                        <ui-grid cols="1" gap="small">
                                            <div>
                                                <ui-badge :class="getVulnerabilityClass(vuln.impact)">
                                                    {{ vuln.impact || 'UNKNOWN' }}
                                                </ui-badge>
                                                <ui-text weight="bold">{{ vuln.title || 'Unnamed Vulnerability' }}</ui-text>
                                            </div>
                                            <ui-text v-if="vuln.description" size="small">
                                                {{ vuln.description }}
                                            </ui-text>
                                        </ui-grid>
                                    </ui-list-item>
                                </ui-list>
                            </div>
                        </ui-grid>
                    </ui-card-content>
                </ui-card>
                
                <!-- Trust Instances Section -->
                <ui-card class="trust-instances-section">
                    <ui-card-header>
                        <ui-heading level="3">
                            Trust Instances
                            <ui-badge type="info">
                                Showing {{ trustInstances.length }} of {{ pagination.totalCount }}
                            </ui-badge>
                        </ui-heading>
                    </ui-card-header>
                    <ui-card-content>
                        <div v-if="loading && trustInstances.length === 0" class="loading-state">
                            <ui-spinner></ui-spinner>
                            <ui-text>Loading trust instances...</ui-text>
                        </div>
                        
                        <div v-else-if="!hasInstances" class="empty-state">
                            <ui-text type="secondary">No trust instances found.</ui-text>
                        </div>
                        
                        <div v-else class="instances-list">
                            <apck-trust-instance-details
                                v-for="(instance, index) in trustInstances"
                                :key="index"
                                :instance="instance"
                                :index="index">
                            </apck-trust-instance-details>
                            
                            <!-- Load More Button -->
                            <div v-if="showLoadMore" class="load-more-section">
                                <ui-button
                                    @click="loadMore"
                                    :disabled="loadingMore"
                                    variant="secondary"
                                    size="medium">
                                    <ui-spinner v-if="loadingMore" size="small"></ui-spinner>
                                    <span v-else>Load More Instances</span>
                                </ui-button>
                                <ui-text type="secondary" size="small">
                                    {{ trustInstances.length }} of {{ pagination.totalCount }} loaded
                                </ui-text>
                            </div>
                            
                            <!-- All Loaded Message -->
                            <div v-else-if="trustInstances.length === pagination.totalCount && pagination.totalCount > 0" class="all-loaded">
                                <ui-text type="secondary" size="small">
                                    All {{ pagination.totalCount }} trust instances loaded
                                </ui-text>
                            </div>
                        </div>
                    </ui-card-content>
                </ui-card>
            </div>
        </div>
    `
};

export default ApckTrustRelationshipDetail;
